// El aviso de "hay algo generándose de fondo" —
// ADR-2026-09-06-el-aviso-de-generando-de-fondo.
//
// Las tandas siguen corriendo cuando el usuario sale del modo (el registro vive a nivel
// app y el reanudador está en el shell), pero nada lo decía: en Creaciones, en Inicio, en
// un modo viejo sin columna de resultados, una generación en curso era invisible y se
// daba por perdida. Esto la nombra: "Generando 3 imágenes · Fotos de producto".
//
// Sólo informa. No navega ni se cierra a mano: se va solo cuando llega la última imagen.
// Y se calla mientras alguna pantalla ya está mostrando la carga —la columna del modo, la
// pantalla de carga full-screen—, porque ahí sería el mismo dato dos veces.
//
// Dos formas, una por plataforma:
//   • Desktop: card flotante abajo a la derecha.
//   • Mobile: header fijo arriba, con el mismo degradé que la barra de créditos, debajo
//     de ella si las dos están. Publica su alto en `--aviso-generando-mobile`, igual que
//     la barra publica el suyo, y el header del shell y el padding del contenido lo leen.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  avisoGenerandoDeFondo,
  textoAvisoGenerando,
} from '../pages/v2/generacionesEnCurso';
import {
  useCompanyIdDelUsuario,
  useGeneracionesDeEmpresa,
  usePantallasDeCarga,
} from '../pages/v2/useGeneracionesEnCurso';
import { MODE_KIND_TO_PATH, nombreCortoModo } from '../pages/v2/modesCatalog';

/** El punto que late: el mismo gesto de "está pasando algo" de los recuadros. Lo
 *  reusa la línea del chat (ADR-2026-10-05-el-aviso-de-generando-no-se-monta-sobre-el-chat). */
export const PuntoPulsante: React.FC<{ className?: string }> = ({ className = '' }) => (
  <span className={`relative inline-flex h-2.5 w-2.5 shrink-0 ${className}`}>
    <span className="absolute inset-0 animate-ping rounded-full bg-current opacity-60" />
    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-current" />
  </span>
);

/**
 * El texto del aviso ("Generando 3 imágenes · Fotos de producto"), o `null` si no hay
 * nada generándose de fondo. Con `ignorarPantallas` no se calla aunque una pantalla
 * esté mostrando la carga: lo usa la conversación del chat, que es una de esas
 * pantallas y dibuja el mismo dato como una línea propia
 * (ADR-2026-10-05-el-aviso-de-generando-no-se-monta-sobre-el-chat).
 */
export function useAvisoGenerando({ ignorarPantallas = false } = {}): string | null {
  const companyId = useCompanyIdDelUsuario();
  const lista = useGeneracionesDeEmpresa(companyId);
  const pantallas = usePantallasDeCarga();
  return useMemo(() => {
    const aviso = avisoGenerandoDeFondo(lista, ignorarPantallas ? 0 : pantallas);
    if (!aviso) return null;
    return textoAvisoGenerando(
      aviso.imagenes,
      aviso.videos,
      aviso.modo ? nombreCortoModo(MODE_KIND_TO_PATH[aviso.modo] ?? '', '') || null : null,
    );
  }, [lista, pantallas, ignorarPantallas]);
}

export const AvisoGenerandoDeFondo: React.FC<{ enEscritorio: boolean }> = ({
  enEscritorio,
}) => {
  const texto = useAvisoGenerando();
  const aviso = texto !== null;

  /**
   * En mobile el header es `fixed` y no empuja nada: publica su alto en
   * `--aviso-generando-mobile` para que el header del shell (`Sidebar`) y el padding del
   * contenido (`DashboardLayout`) bajen lo mismo. Se MIDE porque el texto puede envolver
   * en dos líneas. Sin aviso —o en desktop— la variable se borra y todo vuelve a su lugar.
   */
  const refHeader = useRef<HTMLDivElement | null>(null);
  const visibleEnMobile = !enEscritorio && !!aviso;
  /**
   * ¿Hay una barra de créditos encima? Se lee de la variable que ella publica. Decide
   * quién cubre el notch: el que está más arriba lleva el `padding-top` del safe-area, y
   * si es la barra de créditos, este header no lo repite.
   */
  const [debajoDeCreditos, setDebajoDeCreditos] = useState(false);
  useEffect(() => {
    const raiz = document.documentElement;
    const limpiar = () => raiz.style.removeProperty('--aviso-generando-mobile');
    const el = refHeader.current;
    if (!visibleEnMobile || !el) {
      limpiar();
      return;
    }
    const medir = () => {
      raiz.style.setProperty('--aviso-generando-mobile', `${el.offsetHeight}px`);
      const creditos = parseFloat(
        getComputedStyle(raiz).getPropertyValue('--barra-creditos-mobile') || '0',
      );
      setDebajoDeCreditos(creditos > 0);
    };
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(el);
    window.addEventListener('resize', medir);
    return () => {
      observador.disconnect();
      window.removeEventListener('resize', medir);
      limpiar();
    };
  }, [visibleEnMobile]);

  if (!aviso) return null;

  if (enEscritorio) {
    return (
      // `z-[150]`: arriba del panel del modo (`z-[100]`, para verse también en los modos
      // viejos que no tienen columna) y debajo de los sheets (`z-[210]`), que sí tapan.
      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed bottom-6 right-6 z-[150] inline-flex items-center gap-2.5 rounded-2xl bg-white px-4 py-3 text-[13.5px] font-semibold text-brand-black/85 shadow-[0_12px_40px_rgba(2,6,43,0.14)] ring-1 ring-black/[0.06]"
      >
        <PuntoPulsante className="text-brand-primary" />
        {texto}
      </div>
    );
  }

  return (
    // Debajo de la barra de créditos cuando la hay (`--barra-creditos-mobile`, que ella
    // publica) y arriba del header del shell (`z-50`), que se corre leyendo la variable
    // que publica este. `pt-[env(...)]` sólo cuando este es el de más arriba: con la
    // barra de créditos encima, el notch ya lo cubre ella.
    <div
      ref={refHeader}
      role="status"
      aria-live="polite"
      className={`barra-creditos-degrade fixed inset-x-0 z-[55] bg-brand-primary ${
        debajoDeCreditos ? '' : 'pt-[env(safe-area-inset-top,0px)]'
      }`}
      style={{ top: 'var(--barra-creditos-mobile, 0px)' }}
    >
      <div className="relative z-10 flex items-center gap-2.5 px-4 py-2.5 text-[13.5px] font-semibold text-white/95">
        <PuntoPulsante className="text-white" />
        {texto}
      </div>
    </div>
  );
};

export default AvisoGenerandoDeFondo;
